'use client';

import React, { useState } from 'react';
import classNames from 'classnames';
import { useModalController } from '@/components/molecules/Modal';
import { CreateAIPageModal } from '@/components/organisms/CreateAIPageModal/CreateAIPageModal';
import { useDashboard, usePage, usePageActions, usePageOperations } from '@/modules';
import { useAuth } from '@/modules/auth';
import { useNavigate } from '@/hooks/useNavigate';

interface IEmptyStateProps {
    onAddWidget: () => void;
}

const CheckCircleIcon = () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="8" cy="8" r="7" stroke="#1f8894" strokeWidth="1.5" />
        <path d="M5 8L7 10L11 6" stroke="#1f8894" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const GridIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="3" y="3" width="7" height="7" rx="1.5" stroke="#5a717a" strokeWidth="1.5" />
        <rect x="14" y="3" width="7" height="7" rx="1.5" stroke="#5a717a" strokeWidth="1.5" />
        <rect x="3" y="14" width="7" height="7" rx="1.5" stroke="#5a717a" strokeWidth="1.5" />
        <rect x="14" y="14" width="7" height="7" rx="1.5" stroke="#5a717a" strokeWidth="1.5" />
    </svg>
);

const SparklesIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 2L14.5 9.5L22 12L14.5 14.5L12 22L9.5 14.5L2 12L9.5 9.5L12 2Z" stroke="#1f8894" strokeWidth="1.5" strokeLinejoin="round" />
        <path d="M5 3L5.5 5L7.5 5.5L5.5 6L5 8L4.5 6L2.5 5.5L4.5 5L5 3Z" fill="#1f8894" opacity="0.6" />
    </svg>
);

const EmptyState = ({ onAddWidget }: IEmptyStateProps) => {
    const aiModalController = useModalController();
    const [isCreating, setIsCreating] = useState(false);

    const { state: { pendingNewPage } } = usePage();
    const { setPendingNewPage } = usePageActions();
    const { createPage } = usePageOperations();
    const { state: { currentDashboard } } = useDashboard();
    const { user } = useAuth();
    const navigate = useNavigate();

    const handleBuildManually = async () => {
        if (pendingNewPage && currentDashboard) {
            setIsCreating(true);
            const page = await createPage(pendingNewPage.title, currentDashboard.id, {
                createdBy: user?.userId,
                publishedToAll: pendingNewPage.publishedToAll,
            });
            setPendingNewPage(null);
            setIsCreating(false);
            if (page) {
                navigate(`/dashboard/?pageId=${page.id}`);
                onAddWidget();
            }
        } else {
            onAddWidget();
        }
    };

    const handleGenerateWithAI = () => {
        aiModalController.open();
    };

    return (
        <>
            <div className={classNames('w-full', 'flex', 'items-center', 'justify-center', 'py-16', 'px-4')}>
                <div className="w-full max-w-[820px]">
                    {/* Header */}
                    <div className="text-center mb-10">
                        <h1 className="text-2xl font-semibold text-[#2d3a3f] mb-2">
                            How would you like to create this dashboard?
                        </h1>
                        <p className="text-sm text-gray-500">
                            Build it manually with widgets or let AI generate a ready-to-use layout tailored to your dealership marketing goals.
                        </p>
                    </div>

                    {/* Two-column cards */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-0 border border-gray-200 rounded-xl overflow-hidden bg-white">
                        {/* Build with Widgets */}
                        <div className="p-8 flex flex-col border-b md:border-b-0 md:border-r border-gray-200">
                            <div className="mb-5">
                                <div className="w-10 h-10 rounded-lg bg-gray-50 flex items-center justify-center">
                                    <GridIcon />
                                </div>
                            </div>
                            <h2 className="text-lg font-semibold text-[#2d3a3f] mb-2">Build with Widgets</h2>
                            <p className="text-sm text-gray-500 mb-6">
                                Add and arrange widgets manually. Full control over layout and metrics.
                            </p>
                            <ul className="space-y-3 mb-8 flex-1">
                                <li className="flex items-center gap-2 text-sm text-[#2d3a3f]">
                                    <span className="text-gray-400">•</span>
                                    Choose specific KPIs
                                </li>
                                <li className="flex items-center gap-2 text-sm text-[#2d3a3f]">
                                    <span className="text-gray-400">•</span>
                                    Customize layout
                                </li>
                                <li className="flex items-center gap-2 text-sm text-[#2d3a3f]">
                                    <span className="text-gray-400">•</span>
                                    Start from scratch
                                </li>
                            </ul>
                            <button
                                onClick={handleBuildManually}
                                disabled={isCreating}
                                className="w-full py-2.5 px-4 rounded-lg border border-gray-300 text-sm font-medium text-[#2d3a3f] bg-white hover:bg-gray-50 transition-colors disabled:opacity-50"
                            >
                                {isCreating ? 'Creating...' : 'Start Building'}
                            </button>
                        </div>

                        {/* Generate with AI */}
                        <div className="p-8 flex flex-col relative">
                            <div className="absolute top-4 right-4">
                                <span className="text-[10px] font-semibold uppercase tracking-wider bg-[#1f8894] text-white px-2.5 py-1 rounded-full">
                                    Recommended
                                </span>
                            </div>
                            <div className="mb-5">
                                <div className="w-10 h-10 rounded-lg bg-teal-50 flex items-center justify-center">
                                    <SparklesIcon />
                                </div>
                            </div>
                            <h2 className="text-lg font-semibold text-[#2d3a3f] mb-2">Generate with AI</h2>
                            <p className="text-sm text-gray-500 mb-6">
                                Define your objectives and AI will generate a structured dashboard in seconds.
                            </p>
                            <ul className="space-y-3 mb-8 flex-1">
                                <li className="flex items-center gap-2.5 text-sm text-[#2d3a3f]">
                                    <CheckCircleIcon />
                                    Optimized for automotive agencies
                                </li>
                                <li className="flex items-center gap-2.5 text-sm text-[#2d3a3f]">
                                    <CheckCircleIcon />
                                    Includes ads, leads & inventory insights
                                </li>
                                <li className="flex items-center gap-2.5 text-sm text-[#2d3a3f]">
                                    <CheckCircleIcon />
                                    Fully editable after generation
                                </li>
                            </ul>
                            <button
                                onClick={handleGenerateWithAI}
                                className="w-full py-2.5 px-4 rounded-lg text-sm font-medium text-white bg-[#1f8894] hover:bg-[#1a7580] transition-colors"
                            >
                                Generate Dashboard
                            </button>
                        </div>
                    </div>

                    {/* Footer note */}
                    <p className="text-center text-xs text-gray-400 mt-6">
                        You can customize or add widgets at any time.
                    </p>
                </div>
            </div>

            <CreateAIPageModal
                controller={aiModalController}
                pendingNewPage={pendingNewPage || undefined}
            />
        </>
    );
};

export { EmptyState as NewPageEmptyState };
